import React, { ReactElement, useEffect, useState } from 'react'
import Menu from './Menu'
import Header from './Header'
import Main from './Main'
import Auth from './Auth'
import SipConn from '~/service/SipConn'
import { get } from '~/utils/api'
import CampaignManager from '~/service/CampaignManager'
import ClientListManager from '~/service/ClientListManager'
import TicketListManager from '~/service/TicketListManager'
import CallListManager from '~/service/CallListManager'
import ChatHistoryManager from '~/service/ChatHistoryManager'
import InternalChatManager from '~/service/InternalChatManager'
import VolumeMeter from '~/service/VolumeMeter'
import WebsocketConn from '~/service/WebsocketConn'
import IframeConn from '~/service/IframeConn'
import { Loading } from './common/Loading'
import CallSubjectManager, {
  CallSubjectRaw,
} from '~/service/CallSubjectManager'
import CallTmStatusManager, {
  CallTmStatus,
} from '~/service/CallTmStatusManager'
import LanguageContainer from '~/translations/LanguageContainer'
import AddressBookManager, {
  AddressBook,
  AddressBookContact,
} from '~/service/AddressBookManager'
import UsersManager from '~/service/UsersManager'
import Pause from '~/service/Pause'
import { NotificationsPromptAlert } from './NotificationsPrompt'
import * as Sentry from '@sentry/react'
import ChatConn from '~/service/chatConn'
import VideoConn from '~/service/videoConn'
import Notifications from '~/service/Notifications'
import Video from './Video'
import isMobile from 'ismobilejs'
import AntdProvider from '~/service/AntdProvider'

window.isMobile = isMobile(window.navigator)

export type CampaignApi = {
  id: number
  name: string
  disabled: boolean
  totalCount: number
  calledCount: number
}

type TicketView = {
  id: number
  name: string
  title: string | null
  reportedBy: number | null
  mailAccount: number | null
  status: string | null
  tags: string | null
  hidden: boolean
}

type TicketCategory = {
  defaultStatus: number | null
  deleted: boolean
  disabledNotifications: boolean
  icon: string | null
  id: number
  name: string
  readOnly: boolean
  statuses: any[]
  subcategories: any[]
}

type User = {
  admin: boolean
  avatar: string
  guest: boolean
  id: number
  name: string
  online: boolean
  removed: boolean
  supervisor: boolean
  chatAccounts: [
    {
      id: number
      login: string
      organisationId: number
    }
  ]
}

type InitResp =
  | false
  | {
      userId: number
      campaignId: number
      sip: {
        login: string
        password: string
        uuid: string
        domain: string
        iceServers: IceServer[]
      }
      campaigns: CampaignApi[]
      callSubjects: CallSubjectRaw[]
      callTmStatuses: CallTmStatus[]
      addressBookContacts: AddressBookContact[]
      addressBooks: AddressBook[]
      itsm: boolean
      chatEnabled: boolean
      phoneEnabled: boolean
      chatAttachments: boolean
      janus: boolean
      chat_accounts: ChatAccount[]
      lang: string
      phonePauseEnabled: boolean
      notificationSound: number
      timezone: string
      openAi: boolean
      openAiTopics: OpenAiTopic[]
      openAiChat: OpenAiChatSettings
      llama: boolean
      views: {
        tickets: TicketView[]
      }
      maxFileSize: {
        livechat: number
      }
      reportTypes: Object
      mailAccounts: Array<any>
      ticketCategories: TicketCategory[]
      users: User[]
      user: User
      anonClientNextNumber: number
      ticketSortSettings: {
        urgentFirst: boolean
        unreadFirst: boolean
      }
      stars: [{ id: number; name: string }]
      internalChatEnabled: boolean
      chatHistoryEnabled: boolean
    }

const compose = (
  contexts: [React.ComponentType, object?, boolean?][],
  children: ReactElement
) =>
  contexts.reverse().reduce(
    (acc, [Context, props = {}, enabled = true]) =>
      enabled ? (
        // @ts-ignore
        <Context {...props}>{acc}</Context>
      ) : (
        acc
      ),
    children
  )

const Components: React.FC = ({}) => {
  const [initData, setInitData] = useState<InitResp>()

  useEffect(() => {
    get<InitResp>('init', {}, false)
      .then(data => {
        if (data) {
          window.c = {
            userId: data.userId,
            campaignId: data.campaignId,
            chatEnabled: data.chatEnabled,
            phoneEnabled: data.phoneEnabled,
            menuVisible:
              !data.itsm || data.phoneEnabled || data.internalChatEnabled,
            itsm: data.itsm,
            janus: data.janus,
            chatAccounts: data.chat_accounts,
            notificationSound: data.notificationSound,
            chatAttachments: data.chatAttachments,
            timezone: data.timezone,
            openAi: data.openAi,
            openAiTopics: data.openAiTopics,
            openAiChat: data.openAiChat,
            llama: data.llama,
            views: {
              tickets: data.views?.tickets?.length ? data.views.tickets : [],
            },
            maxFileSize: data.maxFileSize,
            ticketReportTypes: data.reportTypes,
            mailAccounts: data.mailAccounts,
            ticketCategories: data.ticketCategories,
            users: data.users,
            user: data.user,
            anonClientNextNumber: data.anonClientNextNumber,
            ticketSortSettings: data.ticketSortSettings,
            stars: data.stars,
            internalChat: data.internalChatEnabled,
            chatHistory: data.chatHistoryEnabled,
          }

          Sentry.setUser({
            id: data.userId + '',
            username: data.users.find(u => u.id == data.userId)?.name,
          })
        }

        setInitData(data)
      })
      .catch(reason => {
        console.warn('init fail', reason)
        setInitData(false)
      })
  }, [])

  if (typeof initData == 'undefined') {
    return <Loading />
  }

  if (initData === false) {
    return <Auth />
  }

  // https://github.com/facebook/react/issues/14620#issuecomment-491366098

  return compose(
    [
      [LanguageContainer, { locale: initData.lang, addToWindow: true }],
      [AntdProvider, { locale: initData.lang }],
      [IframeConn],
      [Notifications],
      [WebsocketConn],
      [UsersManager, { users: initData.users }],
      [SipConn, initData.sip, window.c.phoneEnabled],
      [ChatConn, {}, initData.chatEnabled],
      [Pause, { phonePauseEnabled: initData.phonePauseEnabled }],
      [VideoConn],
      [VolumeMeter],
      [ClientListManager],
      [TicketListManager],
      [ChatHistoryManager],
      [InternalChatManager],
      [CampaignManager, { campaigns: initData.campaigns }],
      [CallListManager],
      [
        AddressBookManager,
        {
          addressBooks: initData.addressBooks,
          contacts: initData.addressBookContacts,
        },
      ],
      [CallSubjectManager, { subjects: initData.callSubjects }],
      [CallTmStatusManager, { statuses: initData.callTmStatuses }],
    ],
    <>
      <Header />
      <Main />
      <Video />
      <NotificationsPromptAlert />
      <Menu />
    </>
  )
}

export default Components
